import ApiDetailsReact from '../../views/ApiDetails';
import {Text} from '../../components/Text';
import {Image} from '../../components/Image';
import {getTitleId} from '../../utils/getTitleId';
import {INSTAGRAM_POST_SCRAPER} from './scrapers';
import {Box, List, ListItem} from '@mui/material';
import {HighlightedCode} from '../../components/HighlightedCode';

export const InstagramPostScraper = () => {
  return (
    <ApiDetailsReact
      scraperParams={INSTAGRAM_POST_SCRAPER}
      titles={[
        '## What are the different types of posts on Instagram',
        '## Why you should scrape Instagram posts',
        '## Scraped page',
        '## Returned JSON',
      ]}
    >
      <Text
        h4
        bold
        id={getTitleId('What are the different types of posts on Instagram')}
        py={2}
        component={'h2'}
      >
        What are the different types of posts on Instagram
      </Text>
      <Text body1 py={1}>
        There are two types of posts on the Instagram social network: permanent
        posts and temporary posts called stories. The stories remain visible for
        only 24 hours. Posts typically consist of photos, images, videos, and
        more. According to the website e-monsite.com, there are over 200 million
        Instagram users among businesses worldwide who check at least one
        professional profile every day. And 90% of users follow a company or
        brand on Instagram.
      </Text>
      <Text
        h4
        bold
        id={getTitleId('Why you should scrape Instagram posts')}
        py={2}
        component={'h2'}
      >
        Why you should scrape Instagram posts
      </Text>
      <List component="ul" sx={{listStyleType: 'disc', pl: 3}}>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Identify how competitors advertise their products {'('}frequency and
          number of posts on the products sold, types of photos or videos used
          to present the products{')'}.
        </ListItem>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Identify a new product from the competition.
        </ListItem>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Identify how competitors build a connection with their community
          {'('}quality content disseminating information, emotion, engagement,
          organizing and inviting to live sessions{')'}.
        </ListItem>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Identify how competitors strengthen their brand image {'('}commitment
          to a cause, current events{')'}.
        </ListItem>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Draw inspiration from a marketing campaign by a competitor.
        </ListItem>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Gather consumer reviews on a product promoted by an influencer.
        </ListItem>
        <ListItem sx={{display: 'list-item'}} disableGutters>
          Follow a brand to understand the frequency of its posts and its
          approach to promoting products.
        </ListItem>
      </List>
      <Text h4 bold id={getTitleId('Scraped page')} py={2} component={'h2'}>
        Scraped page
      </Text>
      <Text py={2}>
        Here{"'"}s an example of a page that you can scrape with our Instagram
        post scraper:
      </Text>
      <Image
        src="instagram-example1.webp"
        alt="Instagram post example"
        height={1052}
        width={1920}
        sizes={[320, 640, 960, 1280]}
      />
      <Text py={2}>
        On this page, you can see that we will scrape information about a post
        from the Windows Instagram account. You can view details such as the
        post content, number of likes, comments, date of posting, and any other
        relevant information associated with this post.
      </Text>
      <Text h4 bold id={getTitleId('Returned JSON')} py={2} component={'h2'}>
        Returned JSON
      </Text>
      <Text py={2}>
        When you{"'"}re done scraping, here{"'"}s the JSON that you{"'"}ll get:
      </Text>
      <Box component="pre">
        <HighlightedCode language="json">
          {`{
    "url": "https://www.instagram.com/p/CQd4lfnNwvV/",
    "profile": {
        "id": "2025001897",
        "is_verified": true,
        "profile_pic_url": "https://instagram.foua1-1.fna.fbcdn.net/v/t51.2885-19/207696833_1423225411377790_7822892350095029405_n.jpg?stp=dst-jpg_s150x150&_nc_ht=instagram.foua1-1.fna.fbcdn.net&_nc_cat=110&_nc_ohc=_itKYFRyEToAX9O499i&edm=AP_V10EBAAAA&ccb=7-5&oh=00_AfDeca8P-Mr9RDPrDK-vPy2aflRnpEP6H__ST4tEcSx-gQ&oe=644BDA12&_nc_sid=4f375e",
        "username": "windows",
        "blocked_by_viewer": false,
        "restricted_by_viewer": null,
        "followed_by_viewer": false,
        "full_name": "Windows",
        "has_blocked_viewer": false,
        "is_embeds_disabled": false,
        "is_private": false,
        "is_unpublished": false,
        "requested_by_viewer": false,
        "pass_tiering_recommendation": true,
        "edge_owner_to_timeline_media": {
            "count": 827
        },
        "edge_followed_by": {
            "count": 1978302
        }
    },
    "media_url": "https://instagram.foua1-1.fna.fbcdn.net/v/t51.2885-15/204751774_954721708710290_3071819698407734088_n.jpg?stp=dst-jpg_e15_fr_s1080x1080&_nc_ht=instagram.foua1-1.fna.fbcdn.net&_nc_cat=102&_nc_ohc=RilscmimvHcAX_OEC3N&edm=AP_V10EBAAAA&ccb=7-5&oh=00_AfBYBBwVdgPmlcSsh-Y8dfrBYC51RrjbwTmWfrpfEC9c6Q&oe=6447D905&_nc_sid=4f375e",
    "video_url": "https://instagram.foua1-1.fna.fbcdn.net/v/t50.2886-16/205672842_561535201649323_578508725279520310_n.mp4?efg=eyJ2ZW5jb2RlX3RhZyI6InZ0c192b2RfdXJsZ2VuLjk2MC5mZWVkLmRlZmF1bHQiLCJxZV9ncm91cHMiOiJbXCJpZ193ZWJfZGVsaXZlcnlfdnRzX290ZlwiXSJ9&_nc_ht=instagram.foua1-1.fna.fbcdn.net&_nc_cat=107&_nc_ohc=Qz52sR7m2fsAX-qkhwA&edm=AP_V10EBAAAA&vs=17890644809201097_451212220&_nc_vs=HBksFQAYJEdJcFJRZ3lySWptYXR2NEJBRGIydjkyZVJnY0lia1lMQUFBRhUAAsgBABUAGCRHQjJ2T0F6ek1SaVZxeVFCQUNOMjJqUWszNkpsYmtZTEFBQUYVAgLIAQAoABgAGwAVAAAm2rWQ1oW9vD8VAigCQzMsF0AjMzMzMzMzGBJkYXNoX2Jhc2VsaW5lXzFfdjERAHXqBwA%3D&ccb=7-5&oh=00_AfA1tmoKs9GrAgYLRw6C0j-AP3HUFxBLydp8HprmwEDb2w&oe=6447C993&_nc_sid=4f375e",
    "video_view_count": 92809,
    "video_play_count": "N/A",
    "post_text": "Set a reminder to experience what’s new, live, June 24th at 11 am ET 📅 Link in bio.",
    "likes_amount": 18790,
    "post_timestamp": 1624460446,
    "sponsor": "N/A",
    "sponsor_link": "N/A",
    "comments": [
        {
            "name": "jstn.wgnr",
            "text": "Okay so y'all playing @prettylights"
        },
        {
            "name": "jstn.wgnr",
            "text": "Okay so y'all playing @prettylights"
        },
        {
            "name": "yurytupikov",
            "text": "🔥😍👏"
        },
        {
            "name": "newfashion8605",
            "text": "Sem dúvidas o Windows mais bonito.😍"
        },
        {
            "name": "kimxpcomputerservices",
            "text": "Amazing"
        },
        {
            "name": "kimxpcomputerservices",
            "text": "Amazing"
        },
        {
            "name": "_mustafa_farooq_",
            "text": "@heres_maaz @_hamza__syed_"
        },
        {
            "name": "irf_oualla",
            "text": "@amine_dnt"
        }
    ],
    "comments_count": 417,
    "media": [
        {
            "src": "https://instagram.foua1-1.fna.fbcdn.net/v/t51.2885-15/204751774_954721708710290_3071819698407734088_n.jpg?stp=dst-jpg_e15_fr_s1080x1080&_nc_ht=instagram.foua1-1.fna.fbcdn.net&_nc_cat=102&_nc_ohc=RilscmimvHcAX_OEC3N&edm=AP_V10EBAAAA&ccb=7-5&oh=00_AfBYBBwVdgPmlcSsh-Y8dfrBYC51RrjbwTmWfrpfEC9c6Q&oe=6447D905&_nc_sid=4f375e",
            "config_width": 1080,
            "config_height": 1080
        }
    ],
    "id": "2602485026859191253",
    "external_url": "",
    "display_url": "",
    "biography": "N/A"
}
`}
        </HighlightedCode>
      </Box>
      <Text py={2}>
        With our Instagram post scraper, you can extract data from individual
        posts on the platform. Capture details such as post content, usernames,
        and media attachments. Scrapping Instagram posts allows you to analyze
        user-generated content, understand engagement levels, and gain insights
        into the types of content shared by users on the platform.
      </Text>
    </ApiDetailsReact>
  );
};

export default InstagramPostScraper;
